import type { VehicleCard } from "../../types/vtc";
import { cn, eur } from "../../lib/format";
import { assetUrl } from "../../lib/api";
import { label, useT } from "../../i18n";
import Badge from "../ui/Badge";

interface VehicleCardItemProps {
  vehicle: VehicleCard;
  /** Query string carried to the vehicle page, e.g. the searched dates. */
  search?: string;
  /** Hide the company line and "View all" (already on the company page). */
  hideCompany?: boolean;
  /** Smaller card on phones, so two fit side by side (home page). */
  compact?: boolean;
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function VehicleCardItem({ vehicle, search = "", hideCompany = false, compact = false }: VehicleCardItemProps) {
  const t = useT();
  const name = `${vehicle.brand} ${vehicle.model}`;
  const publicId = vehicle.company.publicId;
  // Visitors see the public number, not the company's name
  const companyName = publicId ? t.common.companyLabel(publicId) : vehicle.company.name;
  const companyHref = publicId && !hideCompany ? `/companies/${publicId}` : null;

  // The whole card opens the vehicle (stretched link); "View all" sits above it
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-line-strong bg-white text-ink transition-colors hover:border-[#C9CACB]">
      <a href={`/vehicles/${vehicle.slug}${search}`} aria-label={name} className="absolute inset-0 z-10" />
      {/* Photo slot */}
      <div className="relative flex aspect-16/10 items-center justify-center bg-linear-to-b from-surface to-[#E2E3E4]">
        {vehicle.photo ? (
          <img src={assetUrl(vehicle.photo)} alt={name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="font-mono text-xs text-faint">{t.common.photo} · {name}</span>
        )}

        <Badge
          tone="green"
          className={cn("absolute left-3 top-3 z-20 gap-1", compact && "max-sm:top-1.5 max-sm:left-1.5 max-sm:px-1.5 max-sm:py-0.5 max-sm:text-[10px]")}
        >
          {t.common.availableUnits(vehicle.availableUnits)}
          {companyHref && (
            <a href={companyHref} className={cn("text-tone-green-fg no-underline hover:underline", compact && "max-sm:hidden")}>
              → {t.common.viewAll}
            </a>
          )}
        </Badge>

        {/* {vehicle.urgent && (
          <span
            className={cn(
              "absolute right-3 top-3 z-20 rounded bg-brand px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-white",
              compact && "max-sm:right-1.5 max-sm:bottom-1.5 max-sm:top-auto max-sm:px-1.5 max-sm:py-0.5 max-sm:text-[9px]",
            )}
          >
            {t.card.sponsored}
          </span>
        )} */}
      </div>

      {/* Body */}
      <div className={cn("flex flex-1 flex-col gap-1.5 px-4 pb-4 pt-4 sm:px-4.5", compact && "max-sm:gap-1 max-sm:p-2.5")}>
        <h3 className={cn("m-0 text-lg font-medium", compact && "max-sm:truncate max-sm:text-sm")}>{name}</h3>

        <div className={cn("text-[13px] text-muted", compact && "max-sm:line-clamp-2 max-sm:text-[11px]")}>
          {label(t.enums.energy, vehicle.energy)} · {label(t.enums.transmission, vehicle.transmission)} ·{" "}
          {vehicle.city}
        </div>

        <div className={cn("mt-2 text-[13px] text-ink-soft", compact && "max-sm:mt-0.5 max-sm:text-[11px]")}>
          {t.card.from}{" "}
          <b className={cn("text-xl font-semibold text-ink", compact && "max-sm:text-base")}>{eur(vehicle.weekly)}</b>
          {t.common.perWeek}
        </div>

        <div className={cn("text-[13px] text-muted", compact && "max-sm:hidden")}>
          {t.card.monthlyAndDeposit(eur(vehicle.monthly), eur(vehicle.deposit))}
        </div>

        {vehicle.company.verified && !hideCompany && (
          <div className={cn("mt-1.5 flex items-center gap-1.5 text-xs text-tone-green-fg", compact && "max-sm:hidden")}>
            <CheckIcon />
            {t.card.verifiedBy(companyName)}
          </div>
        )}

        <span className={cn("mt-auto pt-3", compact && "max-sm:pt-1.5")}>
          <span className={cn("flex h-9 items-center justify-center rounded bg-surface text-sm font-medium transition-colors group-hover:bg-surface-hover", compact && "max-sm:h-8 max-sm:text-xs")}>
            {t.card.view}
          </span>
        </span>
      </div>
    </div>
  );
}
